import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DateTime, Info } from 'luxon';
import { useMemo, useState } from 'react';

import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';

import { Listbox } from './listbox';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * The month grid.
 *
 * Works entirely in `YYYY-MM-DD` strings and never in `Date` objects, because a
 * `Date` carries a time and a timezone and both of those turn "the 9th" into "the
 * 8th at 23:00" for half the world. The week starts where the locale says it starts.
 *
 * Reference: documentation/architecture/widget-architecture.md §3.3.
 */

export interface CalendarProps {
  /**
   * Single-select value (`YYYY-MM-DD`), or null.
   * Ignored when `selectionMode` is `multiple`.
   */
  value?: string | null;
  onChange?: (date: string) => void;
  /** Multi-select values. Used when `selectionMode` is `multiple`. */
  values?: string[];
  onValuesChange?: (dates: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  min?: string;
  max?: string;
  /** Dates that cannot be chosen — a closed period, a fully booked day. */
  disabledDates?: string[];
  /** Dates given a dot: a service, an event, a due pledge. */
  markedDates?: string[];
  /** Month and year pickers, for a date far from today. */
  showMonthYearSelects?: boolean;
  popoverPortal?: PopoverPortalTarget;
  monthLabel?: string;
  yearLabel?: string;
  previousMonthLabel?: string;
  nextMonthLabel?: string;
  className?: string;
}

export const Calendar = ({
  value = null,
  onChange,
  values = [],
  onValuesChange,
  selectionMode = 'single',
  min,
  max,
  disabledDates = [],
  markedDates = [],
  showMonthYearSelects = true,
  popoverPortal = 'body',
  monthLabel,
  yearLabel,
  previousMonthLabel,
  nextMonthLabel,
  className,
}: CalendarProps) => {
  const { locale, timezone } = usePreferences();
  const today = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');
  const multiple = selectionMode === 'multiple';
  const anchorDate = multiple
    ? (values[0] ?? today)
    : (value ?? today);

  const [cursor, setCursor] = useState(() =>
    DateTime.fromISO(anchorDate, { zone: 'utc' }).startOf('month'),
  );

  const weekStart = useMemo(() => firstDayOfWeek(locale), [locale]);
  const disabled = useMemo(() => new Set(disabledDates), [disabledDates]);
  const marked = useMemo(() => new Set(markedDates), [markedDates]);
  const selectedSet = useMemo(
    () => new Set(multiple ? values : value ? [value] : []),
    [multiple, values, value],
  );

  const weekdayNames = useMemo(() => {
    const names = Info.weekdays('short', { locale });
    // Luxon returns Monday-first; rotating rather than re-fetching keeps the header
    // and the grid using one definition of where the week starts.
    return [...names.slice(weekStart - 1), ...names.slice(0, weekStart - 1)];
  }, [locale, weekStart]);

  const days = useMemo(() => buildGrid(cursor, weekStart), [cursor, weekStart]);

  const monthOptions = useMemo(
    () =>
      Info.months('long', { locale }).map((name, index) => ({
        value: index + 1,
        label: name,
      })),
    [locale],
  );

  const yearOptions = useMemo(() => {
    const centre = cursor.year;
    // Wide enough for a birth date and a pledge that runs for years, without a list
    // nobody can scroll.
    return Array.from({ length: 121 }, (_, offset) => centre - 100 + offset).map((year) => ({
      value: year,
      label: String(year),
    }));
  }, [cursor.year]);

  const isOutOfRange = (date: string): boolean =>
    (min !== undefined && date < min) || (max !== undefined && date > max);

  const selectDate = (iso: string) => {
    if (multiple) {
      const next = selectedSet.has(iso)
        ? values.filter((date) => date !== iso)
        : [...values, iso].sort((a, b) => a.localeCompare(b));
      onValuesChange?.(next);
      return;
    }
    onChange?.(iso);
  };

  return (
    <div className={cn('w-72 select-none p-3', className)}>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          aria-label={previousMonthLabel}
          onClick={() => setCursor(cursor.minus({ months: 1 }))}
          className="shrink-0 rounded-md p-1.5 text-fg-secondary hover:bg-surface-sunken hover:text-fg"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {showMonthYearSelects ? (
          <div className="flex min-w-0 flex-1 gap-1.5">
            <Listbox<number>
              value={cursor.month}
              onChange={(month) => month && setCursor(cursor.set({ month }))}
              options={monthOptions}
              size="sm"
              // Long month names (September) must clip, not grow the header and shove
              // the next-month control outside the panel.
              className="min-w-0 flex-1"
              popoverPortal={popoverPortal}
              aria-label={monthLabel}
            />
            <Listbox<number>
              value={cursor.year}
              onChange={(year) => year && setCursor(cursor.set({ year }))}
              options={yearOptions}
              searchable
              size="sm"
              className="w-20 shrink-0"
              popoverPortal={popoverPortal}
              aria-label={yearLabel}
            />
          </div>
        ) : (
          <p className="min-w-0 flex-1 truncate text-center text-body font-medium text-fg">
            {cursor.setLocale(locale).toFormat('LLLL yyyy')}
          </p>
        )}

        <button
          type="button"
          aria-label={nextMonthLabel}
          onClick={() => setCursor(cursor.plus({ months: 1 }))}
          className="shrink-0 rounded-md p-1.5 text-fg-secondary hover:bg-surface-sunken hover:text-fg"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5" role="grid">
        {weekdayNames.map((name) => (
          <div
            key={name}
            role="columnheader"
            className="pb-1 text-center text-overline font-semibold text-fg-tertiary uppercase"
          >
            {name.slice(0, 2)}
          </div>
        ))}

        {days.map((day) => {
          const iso = day.toFormat('yyyy-MM-dd');
          const outsideMonth = day.month !== cursor.month;
          const unavailable = disabled.has(iso) || isOutOfRange(iso);
          const selected = selectedSet.has(iso);
          const isToday = iso === today;

          return (
            <button
              key={iso}
              type="button"
              role="gridcell"
              disabled={unavailable}
              aria-selected={selected}
              aria-current={isToday ? 'date' : undefined}
              onClick={() => selectDate(iso)}
              className={cn(
                'relative flex h-9 items-center justify-center rounded-md text-body-sm transition-colors',
                outsideMonth ? 'text-fg-tertiary' : 'text-fg',
                !unavailable &&
                  !selected &&
                  (isToday ? 'hover:bg-brand/10' : 'hover:bg-surface-sunken'),
                selected && 'bg-brand font-semibold text-on-brand hover:bg-brand-hover',
                isToday &&
                  !selected &&
                  'bg-brand-subtle font-semibold text-brand ring-2 ring-inset ring-brand',
                unavailable && 'cursor-not-allowed text-fg-disabled hover:bg-transparent',
              )}
            >
              {day.day}
              {marked.has(iso) ? (
                <span
                  aria-hidden
                  className={cn(
                    'absolute bottom-1 h-1 w-1 rounded-full',
                    selected ? 'bg-on-brand' : 'bg-brand',
                  )}
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** 1 = Monday … 7 = Sunday, per the locale where the browser can tell us. */
const firstDayOfWeek = (locale: string): number => {
  try {
    const info = new Intl.Locale(locale) as Intl.Locale & {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    return info.getWeekInfo?.().firstDay ?? info.weekInfo?.firstDay ?? 1;
  } catch {
    return 1;
  }
};

/** Always six rows, so the grid does not change height as months change. */
const buildGrid = (month: DateTime, weekStart: number): DateTime[] => {
  const firstOfMonth = month.startOf('month');
  const offset = (firstOfMonth.weekday - weekStart + 7) % 7;
  const start = firstOfMonth.minus({ days: offset });
  return Array.from({ length: 42 }, (_, index) => start.plus({ days: index }));
};
